import {
  StockClass,
  useStockClassList,
  useUpdateShareClassTotals,
} from '@commenda/api';
import {
  Button,
  FieldWrapper,
  Icons,
  Label,
  Loading,
  NumberInput,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@commenda/kakashi';
import { useEffect, useState } from 'react';
import {
  CollapsibleModuleCard,
  FormActions,
} from '../../../form-builder/components/new-incorp-layout';
import { DeleteShareClassConfirm } from '../components/DeleteShareClassConfirm';
import { isCapitalOnlyLlc, useEntityOwnershipNouns } from '../ownershipNouns';
import { friendlyOwnershipMessage } from './applyOwnershipApiError';
import { SUBMIT_VALIDATION_TOAST } from './submitToast';

interface EditShareClassesFormProps {
  corporationId: number;
  onCancel: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export const EditShareClassesForm = ({
  corporationId,
  onCancel,
  onSuccess,
  onError,
}: EditShareClassesFormProps) => {
  const { data: stockClasses, isLoading } = useStockClassList(corporationId);
  const {
    classNouns,
    tracksUnits,
    totalLabel: holdingTotalLabel,
    sectionTitle: holdingSectionTitle,
  } = useEntityOwnershipNouns(corporationId);
  const mutation = useUpdateShareClassTotals(corporationId);
  const canDelete = (stockClasses?.length ?? 0) > 1;
  // An LLC that hasn't named a unit class holds capital, not units — it edits one
  // amount, the same as a partnership, rather than a table of classes it never chose.
  const capitalOnlyLlc = isCapitalOnlyLlc(tracksUnits, stockClasses);
  const totalLabel = capitalOnlyLlc
    ? 'Total initial capital contribution'
    : holdingTotalLabel;
  const sectionTitle = capitalOnlyLlc ? 'Capital' : holdingSectionTitle;
  const minTotalError = `${totalLabel} must be at least 1.`;
  // Same rule as the class picker: only collapse onto a single class.
  const soleClass =
    (classNouns === null || capitalOnlyLlc) && stockClasses?.length === 1
      ? stockClasses[0]
      : undefined;
  const [cardOpen, setCardOpen] = useState(true);
  const [values, setValues] = useState<Record<string, number | undefined>>({});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [pendingDelete, setPendingDelete] = useState<StockClass>();

  // Seed local edits from the loaded list without clobbering in-progress
  // typing if the list refetches mid-edit (only fills in keys not seen yet).
  useEffect(() => {
    if (!stockClasses) return;
    setValues((prev) => {
      const unseen = stockClasses.filter((stockClass) => !(stockClass.id in prev));
      if (unseen.length === 0) return prev;
      const next = { ...prev };
      unseen.forEach((stockClass) => {
        next[stockClass.id] = stockClass.authorizedShares ?? undefined;
      });
      return next;
    });
  }, [stockClasses]);

  const handleChange = (stockClass: StockClass, next: number | null) => {
    setValues((prev) => ({ ...prev, [stockClass.id]: next ?? undefined }));
    setErrors((prev) => ({
      ...prev,
      [stockClass.id]: next !== null && next < 1 ? minTotalError : undefined,
    }));
  };

  const dropClassState = (id: string) => {
    setValues(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setErrors(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const total = (stockClasses ?? []).reduce(
    (sum, stockClass) => sum + (values[stockClass.id] ?? 0),
    0,
  );
  const hasErrors = Object.values(errors).some(Boolean);

  const handleSave = async () => {
    if (!stockClasses || hasErrors) {
      onError?.(SUBMIT_VALIDATION_TOAST);
      return;
    }

    const changed = stockClasses.filter((stockClass) => {
      const value = values[stockClass.id];
      return value !== undefined && value !== stockClass.authorizedShares;
    });
    if (changed.length === 0) {
      onCancel();
      return;
    }

    try {
      await mutation.mutateAsync(
        changed.map((stockClass) => ({
          shareClassId: stockClass.id,
          authorizedShares: values[stockClass.id] as number,
        })),
      );
      onSuccess?.(`${sectionTitle} updated.`);
      onCancel();
    } catch (err) {
      onError?.(friendlyOwnershipMessage(err));
    }
  };

  return (
    <div className="flex flex-col gap-2xl pb-8xl">
      <div className="flex flex-col gap-xs">
        <h1 className="display-xs-bold text-text-primary">
          Edit {sectionTitle.toLowerCase()}
        </h1>
      </div>

      <CollapsibleModuleCard
        title={sectionTitle}
        isExpanded={cardOpen}
        onExpandedChange={setCardOpen}
      >
        {isLoading || !stockClasses ? (
          <div className="flex items-center justify-center py-2xl">
            <Loading />
          </div>
        ) : soleClass ? (
          <FieldWrapper>
            <Label required>{totalLabel}</Label>
            <NumberInput
              value={values[soleClass.id] ?? ''}
              onChange={(next) => handleChange(soleClass, next)}
              placeholder="500,000"
              min={1}
              step={1}
              formatWithCommas
              error={errors[soleClass.id]}
            />
          </FieldWrapper>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Class name</TableHead>
                <TableHead>{totalLabel}</TableHead>
                {canDelete && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {stockClasses.map((stockClass) => (
                <TableRow key={stockClass.id}>
                  <TableCell>{stockClass.name}</TableCell>
                  <TableCell>
                    <NumberInput
                      value={values[stockClass.id] ?? ''}
                      onChange={(next) => handleChange(stockClass, next)}
                      placeholder="10,000,000"
                      min={1}
                      step={1}
                      formatWithCommas
                      error={errors[stockClass.id]}
                    />
                  </TableCell>
                  {canDelete && (
                    <TableCell className="w-px">
                      <Button
                        variant="destructive-tertiary"
                        size="sm"
                        type="button"
                        aria-label={`Delete ${stockClass.name}`}
                        onClick={() => setPendingDelete(stockClass)}
                      >
                        <Icons.Trash01 className="size-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell>Total</TableCell>
                <TableCell className="text-text-tertiary">
                  {total.toLocaleString('en-US')}
                </TableCell>
                {canDelete && <TableCell />}
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </CollapsibleModuleCard>

      <DeleteShareClassConfirm
        isOpen={pendingDelete !== undefined}
        onClose={() => setPendingDelete(undefined)}
        corporationId={corporationId}
        shareClass={pendingDelete}
        classNounLower={classNouns?.lowerSingular ?? 'class'}
        onSuccess={(message) => {
          if (pendingDelete) dropClassState(pendingDelete.id);
          setPendingDelete(undefined);
          onSuccess?.(message);
        }}
        onError={onError}
      />

      <FormActions
        hideBack
        showCancel
        onCancel={onCancel}
        onNext={handleSave}
        nextLabel="Save"
        isLoading={mutation.isPending}
        disableNext={isLoading || hasErrors}
      />
    </div>
  );
};
